import React, { useMemo } from 'react';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { faMinus } from '@fortawesome/pro-light-svg-icons/faMinus';
import { availableSearchSourcesAccept } from 'fym-common/src/types/searchService';
import { TypedMemo } from '../../common/types';
import { IntegrationTypeIcon } from '../../music24/components/IntegrationTypeIcon/IntegrationTypeIcon';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the sheet; each part uses its own slice of it */
import styles from './SMLCreationSheet.module.scss';
/* eslint-enable css-modules/no-unused-class */

type ServiceState = 'found' | 'pending' | 'missing';

interface CreationServiceGridProps {
  found: string[];
  pending: string[];
  missing: string[];
}

function serviceName(source: string): string {
  return availableSearchSourcesAccept[source]?.name ?? source;
}

/**
 * Where the release was found, service by service, while it is being found.
 *
 * The old wizard showed a spinner and then a list. Watching a tick land on
 * Spotify and a dash on Napster tells the artist what the page will be before
 * they create it, which is the whole reason to show it at all.
 *
 * A service we have not heard back about is `pending` and beats; only a service
 * the search has answered for gets the grey dash. The two used to look alike,
 * so a search still running read as sixteen services missing.
 */
const CreationServiceGridComponent: React.FC<CreationServiceGridProps> = ({ found, pending, missing }) => {
  const rows = useMemo(
    () => [
      ...found.map((source) => ({ source, state: 'found' as ServiceState })),
      ...pending.map((source) => ({ source, state: 'pending' as ServiceState })),
      ...missing.map((source) => ({ source, state: 'missing' as ServiceState })),
    ],
    [found, missing, pending]
  );

  return (
    <div className={styles.serviceGrid}>
      {rows.map(({ source, state }) => (
        <div
          key={source}
          className={clsx(
            styles.service,
            state === 'pending' && styles.servicePending,
            state === 'missing' && styles.serviceMissing
          )}
        >
          <IntegrationTypeIcon integrationType={source} size={18} />
          <span className={styles.serviceName}>{serviceName(source)}</span>
          <span className={styles.serviceMark}>
            {state === 'found' && <FontAwesomeIcon icon={faCheck} size="2xs" />}
            {/* A beating dot, not an empty circle: the answer is still coming. */}
            {state === 'pending' && <span className={styles.serviceDot} />}
            {state === 'missing' && <FontAwesomeIcon icon={faMinus} size="2xs" />}
          </span>
        </div>
      ))}
    </div>
  );
};

export const CreationServiceGrid = TypedMemo(CreationServiceGridComponent);
